import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, ShieldCheck, Layers, Users } from "lucide-react";

import Reveal from "@/components/public/Reveal";
import { publicApi } from "@/lib/api";

const SKILL_ICONS = {
  "Customer Focus": Users,
  Professionalism: ShieldCheck,
  Experience: Compass,
  Integrity: Layers,
};

export default function ResidentialPage() {
  const [page, setPage] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    publicApi.track({ event_type: "page_view", page: "/residential-design" }).catch(() => {});
    publicApi.getPage("residential").then(setPage).catch(() => {});
    publicApi.getProjects().then(setProjects).catch(() => {});
  }, []);

  const hero = page?.content?.hero || {
    headline: "Dream Spaces",
    subheadline: "Designing the background for your life’s story.",
    background:
      "/assets/minga/projects/waters-residence/02-dining.jpg",
  };
  const intro = page?.content?.intro;
  const skills = page?.content?.skills || [];

  return (
    <div>
      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${hero.background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/70 via-ink-black/40 to-ink-black" />
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <p className="eyebrow">{hero.eyebrow || "[ Residential Design ]"}</p>
          <h1 className="hero-headline mt-6 max-w-4xl">{hero.headline}</h1>
          <p className="body-text mt-6 max-w-2xl md:text-lg text-brand-white/85">
            {hero.subheadline}
          </p>
        </div>
      </section>

      {intro && (
        <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="body-text text-lg max-w-3xl">{intro}</p>
          </Reveal>
        </section>
      )}

      {/* Portfolio */}
      <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">[ Portfolio ]</p>
          <h2 className="section-headline mt-4 max-w-2xl">Residential Work</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 mt-12">
          {projects.map((p, i) => (
            <Reveal key={p.id} delay={i * 70} className="group">
              <Link
                to={`/residential-design/${p.slug}`}
                className="relative block aspect-[4/3] overflow-hidden bg-ink-graphite image-hover-zoom"
                data-testid={`residential-card-${p.slug}`}
              >
                <img
                  src={p.cover_image}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-black/80 via-ink-black/20 to-transparent" />
                
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex items-end justify-between gap-6">
                  <h3 className="headline text-2xl md:text-3xl">{p.title}</h3>
                  <span className="eyebrow text-brand-yellow flex items-center gap-2">
                    View <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-brand-yellow group-hover:w-full transition-all duration-[900ms]" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Skills */}
      {skills?.length ? (
        <section className="section-pad bg-brand-red text-brand-white border-y border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <Reveal className="mb-12">
              <p className="eyebrow !text-brand-white/80">[ Why Minga ]</p>
              <h2 className="section-headline mt-4 max-w-2xl text-brand-white">Principles that shape the work.</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {skills.map((s, i) => {
                const Icon = SKILL_ICONS[s.title] || Layers;
                return (
                  <Reveal
                    key={s.title}
                    delay={i * 80}
                    className="relative p-6 md:p-7 min-h-[220px] border border-white/20 bg-ink-black/25 overflow-hidden"
                  >
                    <span className="absolute top-0 right-0 h-7 w-7 border-t-2 border-r-2 border-white/70" />
                    <Icon className="h-8 w-8 text-brand-white" />
                    <h3 className="headline text-xl mt-5 text-brand-white">{s.title}</h3>
                    <p className="body-text mt-3 text-sm !text-brand-white/85">{s.text}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      <section className="section-pad overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="section-headline soft-text-reveal dream-space-reveal">
              Let’s design your dream space
            </h2>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
